import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentPreviewDialogComponent } from '../document-preview-dialog/document-preview-dialog.component';
import { Document, IRequest, IYesNo } from 'src/app/shared/interface';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject } from 'rxjs';
import { Api } from 'src/app/shared/constants/api';
import { DataService } from 'src/app/shared/services/data.service';
import { IDocumentCriteria, IKyc } from '../external-entity.interface';
import { Message } from 'src/app/shared/constants/message';
import * as _ from 'underscore';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-kyc-details',
    templateUrl: './kyc-details.component.html',
    styleUrls: ['./kyc-details.component.less'],
    standalone: false
})
export class KycDetailsComponent implements OnInit, OnDestroy {

  constructor(private logger:NGXLogger,
              private service:DataService,
              @Optional() private dailogRef:MatDialogRef<KycDetailsComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data:any,
              private http: HttpClient, private dialog: MatDialog,
              private utilityService:UtilityService, private toastrService: ToastrService) { }

  documentsOnEdit = [];
  documents: any[] = [];
  allDocuments: any[] = [];
  masterDocuments: any[] = [];
  rowSearchCtrls: { [rowId: number]: FormControl } = {};
  rowFilteredDocs: { [rowId: number]: any[] } = {};
  private _onDestroy = new Subject<void>();
  documentDataSource= new BehaviorSubject([]);
  documentCol: any[] = [
    { id: 'documentCode', name: 'Document Code' },
    { id: 'documentName', name: 'Document Name' },
    { id: 'uploadDocument', name: 'Upload' },
    { id: 'kycDoneBy', name: 'KYC Done By' },
    { id: 'kycDateTime', name: 'Date & Time' }
  ];
  KYCColumns:string[] = this.documentCol.map(col => col.id);
  get displayedColumns(): string[] {
    return this.disableTable ? this.KYCColumns : [...this.KYCColumns, 'action'];
  }
  @Input() referenceName:string;
  id:number;
  resData:any;
  documentCriteria:IDocumentCriteria={};
  @Input() buttonHidden:boolean=false;
  @Input() set dataById(id:number){
    if(id){
       this.id=id;
       this.getDataById();
    }
 }
  @Input() set uploadFile(id:number){
     if(id){
        this.uploadDocuments(id);
     }
  }
  @Input() set countryId(value:number){
    if(value){
      this.countryChanged(value); 
    }
  }
  @Input() disableTable:boolean;
  loggedInUser: any;

  ngOnInit(): void {
    this.getLoggedInUser();
    this.loadDocumentMaster();
    this.setValueFromData();
    this.documentCriteria.referenceId = this.data?.countryId;
    this.getDataById();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private getLoggedInUser() {
    this.service.get(Api.LOGGED_IN_USER).subscribe(res => {
      if (res) { this.loggedInUser = res; }
    });
  }

  private loadDocumentMaster() {
    this.service.get(Api.DOCUMENT_MASTER_FOR_DD).subscribe((res: any[]) => {
      if (res && res.length) {
        this.masterDocuments = res.map((d: any) => ({ code: d.code, name: d.name, id: d.id }));
        this.allDocuments = [...this.masterDocuments];
        this._refreshRowFilteredDocs();
      }
    });
  }

  getRowSearchCtrl(rowId: number): FormControl {
    if (!this.rowSearchCtrls[rowId]) {
      const ctrl = new FormControl('');
      ctrl.valueChanges.pipe(debounceTime(200), takeUntil(this._onDestroy)).subscribe(search => {
        if (!search || typeof search !== 'string') {
          this.rowFilteredDocs[rowId] = [...this.allDocuments];
          return;
        }
        const s = search.toLowerCase();
        this.rowFilteredDocs[rowId] = this.allDocuments.filter(d => d.name?.toLowerCase().includes(s));
      });
      this.rowSearchCtrls[rowId] = ctrl;
      this.rowFilteredDocs[rowId] = [...this.allDocuments];
    }
    return this.rowSearchCtrls[rowId];
  }

  getRowFilteredDocs(rowId: number): any[] {
    if (!this.rowFilteredDocs[rowId]) {
      this.rowFilteredDocs[rowId] = [...this.allDocuments];
    }
    return this.rowFilteredDocs[rowId];
  }

  clearRowSearch(rowId: number): void {
    const ctrl = this.rowSearchCtrls[rowId];
    if (ctrl) { ctrl.setValue('', { emitEvent: false }); }
    this.rowFilteredDocs[rowId] = [...this.allDocuments];
  }

  private _refreshRowFilteredDocs(): void {
    Object.keys(this.rowFilteredDocs).forEach(key => {
      const id = Number(key);
      if (!this.rowSearchCtrls[id]?.value) {
        this.rowFilteredDocs[id] = [...this.allDocuments];
      }
    });
  }

  private resolveDocName(code: string, fallback?: string): string {
    return this.masterDocuments?.find(m => m.code === code)?.name || fallback || code;
  }

  /** Extract the actual document code from the stored type value.
   *  Entity_KYC:        'Entity_PAN'        → 'PAN'
   *  Entity_Branch_KYC: 'Entity_Branch_PAN' → 'PAN'
   */
  private resolveCodeFromType(type: string): string {
    if (!type) return '';
    if (type.startsWith('Entity_Branch_')) return type.slice('Entity_Branch_'.length);
    if (type.startsWith('Entity_')) return type.slice('Entity_'.length);
    return '';
  }

  /** Fix the `code` field on each documentsOnEdit entry using the stored `type`. */
  private normalizeDocumentsOnEdit(): void {
    this.documentsOnEdit = (this.documentsOnEdit || []).map((d: any) => {
      const actualCode = this.resolveCodeFromType(d.type) || d.code;
      return { ...d, code: actualCode };
    });
  }

  setValueFromData(){
    if(this.data?.branchId){
      this.id=this.data?.branchId
    }
    if(this.data?.countryId){
      this.countryChanged(this.data?.countryId);
    }
    if(this.data?.referenceName){
      this.referenceName=this.data?.referenceName;
    }
  }

  onSave(){
     this.uploadDocuments(this.id);
  }

  getDataById(){
    if(this.id){
      let url=(this.referenceName==='External Entity'||this.referenceName==='Entity_KYC')?Api.EXTERNAL_ENTITY:Api.ENTITY_BRANCH;
      let requests: IRequest[] = [{
        url: url+'/'+this.id, method: 'get'
      },{
        url: `${Api.DOCUMENT_STORAGE_GET}${this.referenceName}/${this.id}`, method: 'get'
      }]
      
      this.service.forkJoin(requests).subscribe(res=>{
        this.logger.info("KYC Documents Response - ", res);
          this.resData = res[0];
          const allDocs = res[1] || [];
          this.documentsOnEdit = allDocs || [];
          this.normalizeDocumentsOnEdit();
          const countryId = this.resData?.address?.countryId;
          if (countryId) {
            this.countryChanged(countryId);
          } else {
            this.mergeUploadedIntoCurrentRows();
          }
      })
    }
  }

  onCancel(){
    this.dailogRef?.close();
  }

  downloadFile(row) {
    console.log('Request to download a file ', row);
     if(this.resData?.submittedForApproval){ return; }
     if (this.documentsOnEdit) {
      let selectedDocument = this.getDocument(row);
      if(selectedDocument){
        this.service.donwload(selectedDocument.fileName).subscribe((data: any) => {
            if (data) {
              let url1: string = window.URL.createObjectURL(data);
              window.open(url1);
            }
        });
      }
    }
  }

  previewFile(row: any) {
    // Freshly added file (not yet saved) — preview from the staged base64 content
    const pending = this.documents?.find(d => d._rowId === row._rowId);
    if (pending?.fileString) {
      const blob = this.base64ToBlob(pending.fileString, pending.contentType);
      this.openPreviewDialog(blob, pending.fileName || row.documentName, pending.contentType);
      return;
    }
    // Saved file — fetch the stored blob and preview it in a popup
    const selectedDocument = this.getDocument(row);
    if (selectedDocument?.fileName) {
      this.service.donwload(selectedDocument.fileName).subscribe({
        next: (data: Blob) => {
          if (data) {
            this.openPreviewDialog(data, selectedDocument.originalFileName || row.documentName,
              this.getMimeType(selectedDocument.originalFileName));
          }
        },
        error: () => {
          this.toastrService.error('Unable to preview this document. The file could not be retrieved from storage.');
        }
      });
    }
  }

  private openPreviewDialog(blob: Blob, fileName: string, contentType: string) {
    const mime = contentType || this.getMimeType(fileName);
    const isImage = mime.startsWith('image/');
    // Re-wrap with the resolved mime so the browser renders it (server may return octet-stream)
    const typedBlob = new Blob([blob], { type: mime });
    const url = window.URL.createObjectURL(typedBlob);
    const ref = this.dialog.open(DocumentPreviewDialogComponent, {
      data: { url, fileName, isImage },
      maxWidth: '95vw',
      maxHeight: '95vh'
    });
    ref.afterClosed().subscribe(() => window.URL.revokeObjectURL(url));
  }

  private getMimeType(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'png':  return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif':  return 'image/gif';
      case 'bmp':  return 'image/bmp';
      case 'webp': return 'image/webp';
      case 'pdf':  return 'application/pdf';
      default:     return 'application/octet-stream';
    }
  }

  downloadKycFile(row: any) {
    // Freshly added file (not yet saved) — download from the staged base64 content
    const pending = this.documents?.find(d => d._rowId === row._rowId);
    if (pending?.fileString) {
      const blob = this.base64ToBlob(pending.fileString, pending.contentType);
      this.triggerDownload(blob, pending.fileName || row.documentName);
      return;
    }
    // Saved file — fetch the stored blob and save it with its original name
    const selectedDocument = this.getDocument(row);
    if (selectedDocument?.fileName) {
      this.service.donwload(selectedDocument.fileName).subscribe({
        next: (data: Blob) => {
          if (data) {
            this.triggerDownload(data, selectedDocument.originalFileName || row.documentName);
          }
        },
        error: () => {
          this.toastrService.error('Unable to download this document. The file could not be retrieved from storage.');
        }
      });
    }
  }

  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName || 'document';
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  getDocument(row: any) {
    if (!this.documentsOnEdit?.length) return null;
    if (row._docId != null) {
      return this.documentsOnEdit.find(item => item.id === row._docId) || null;
    }
    return null;
  }

  countryChanged(countryId){
    this.service.get(Api.COUNTRY+'/'+countryId).subscribe(response=>{
      if(response.documents && response.documents.length){
        // Dropdown options = country docs + any already-uploaded docs (so every row's
        // code has a matching option and shows its friendly name).
        const optionMap = new Map<string, any>();
        response.documents.forEach((d:any) => optionMap.set(d.code, { code: d.code, name: d.name }));
        this.documentsOnEdit?.forEach((d:any) => {
          if (!optionMap.has(d.code)) {
            optionMap.set(d.code, { code: d.code, name: this.resolveDocName(d.code, d.name) });
          }
        });
        this.allDocuments = Array.from(optionMap.values());
        this._refreshRowFilteredDocs();

        let rowIdCounter = 0;
        let documentList: any[] = [];

        response.documents.forEach((element: any) => {
          const uploads = this.documentsOnEdit?.filter((d: any) => d.code === element.code) || [];
          if (uploads.length > 0) {
            uploads.forEach((uploaded: any) => {
              documentList.push({
                _rowId: rowIdCounter++,
                _docId: uploaded.id,
                documentCode: element.code,
                documentName: element.name,
                isMandatory: element.isMandatory,
                fileExists: true,
                newFileName: uploaded.originalFileName || '',
                kycDoneBy: this.loggedInUser?.name || '',
                kycDateTime: this.formatDateTime(new Date())
              } as Document);
            });
          } else {
            documentList.push({
              _rowId: rowIdCounter++,
              _docId: null,
              documentCode: element.code,
              documentName: element.name,
              isMandatory: element.isMandatory,
              fileExists: false,
              newFileName: '',
              kycDoneBy: '',
              kycDateTime: ''
            } as Document);
          }
        });

        const configCodes = new Set(response.documents.map((d: any) => d.code));
        this.documentsOnEdit
          ?.filter((d: any) => !configCodes.has(d.code))
          .forEach((d: any) => {
            documentList.push({
              _rowId: rowIdCounter++,
              _docId: d.id,
              documentCode: d.code,
              documentName: this.resolveDocName(d.code, d.documentName || d.name),
              isMandatory: false,
              fileExists: true,
              newFileName: d.originalFileName || '',
              kycDoneBy: this.loggedInUser?.name || '',
              kycDateTime: this.formatDateTime(new Date()),
              isManual: true
            } as Document);
          });
        this.documents=[];
        this.documentDataSource.next(documentList);
      } else {
        this.mergeUploadedIntoCurrentRows();
      }
    })
  }


  private mergeUploadedIntoCurrentRows() {
    if (!this.documentsOnEdit?.length) return;
    const current = this.documentDataSource.getValue();
    if (current.length === 0) {
      // No country-linked documents exist; create one row per uploaded document
      let rowIdCounter = 0;
      const rows = this.documentsOnEdit.map((d: any) => ({
        _rowId: rowIdCounter++,
        _docId: d.id,
        documentCode: d.code,
        documentName: this.resolveDocName(d.code, d.documentName || d.name),
        isMandatory: false,
        fileExists: true,
        newFileName: d.originalFileName || '',
        kycDoneBy: this.loggedInUser?.name || '',
        kycDateTime: this.formatDateTime(new Date()),
        isManual: true
      }));
      this.documentDataSource.next(rows);
    } else {
      const merged = current.map((row: any) => {
        const uploaded = row._docId != null
          ? this.documentsOnEdit.find((d: any) => d.id === row._docId)
          : this.documentsOnEdit.find((d: any) => d.code === row.documentCode);
        if (uploaded) {
          return { ...row, fileExists: true, newFileName: uploaded.originalFileName || '', kycDoneBy: this.loggedInUser?.name || '', kycDateTime: this.formatDateTime(new Date()) };
        }
        return row;
      });
      this.documentDataSource.next(merged);
    }
  }

  onDocumentSelect(selectedName: string, row: any) {
    if (this.disableTable) { return; }
    const doc = this.allDocuments.find(d => d.name === selectedName);
    if (doc) {
      row.documentCode = doc.code;
      row.documentName = doc.name;
    }
  }

  onDocumentCodeChange(code: string, row: any) {
    if (this.disableTable) { return; }
    const doc = this.allDocuments.find(d => d.code === code?.trim().toUpperCase());
    if (doc) {
      row.documentName = doc.name;
      this.documentDataSource.next([...this.documentDataSource.getValue()]);
    }
  }

  addDocumentRow(){
    if (this.disableTable) { return; }
    const current = this.documentDataSource.getValue();
    const nextRowId = current.reduce((max, r) => Math.max(max, r._rowId ?? -1), -1) + 1;
    current.push({ _rowId: nextRowId, _docId: null, documentCode: '', documentName: '', isMandatory: false, fileExists: false, kycDoneBy: '', kycDateTime: '', isManual: true });
    this.documentDataSource.next([...current]);
  }

  deleteDocumentRow(index: number): void {
    if (this.disableTable) { return; }
    const current = this.documentDataSource.getValue();
    const row = current[index];
    if (row?._rowId != null) {
      this.documents = this.documents.filter(d => d._rowId !== row._rowId);
    }
    current.splice(index, 1);
    this.documentDataSource.next([...current]);
  }

  private formatDateTime(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${mm}`;
  }


  onUploadClick(row: any, fileInput: HTMLInputElement) {
    if (this.disableTable) { return; }
    if (!row?.documentCode) {
      this.utilityService.notifier(Message.KYC_SELECT_DOCUMENT_FIRST, 'info');
      return;
    }
    fileInput.click();
  }

  filesSelected(row, files){
    if(this.resData?.submittedForApproval || this.disableTable){ return; }
    if (!files || files.length === 0) return;

    const file = files[0];
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSizeBytes = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastrService.error('Only PDF, JPG and PNG files are allowed.');
      return;
    }
    if (file.size > maxSizeBytes) {
      this.toastrService.error('File size must not exceed 5 MB.');
      return;
    }

    if (row && row.documentCode) {
      this.convertFileToBase64(file).then(base64 => {
        const documentObj = {
          _rowId: row._rowId,
          code: this.getDefinitionCode(),
          documentName: row.documentName,
          referenceName: this.referenceName,
          type: this.getDocumentType(row.documentCode),
          isDeleted: false,
          contentType: file.type,
          fileName: file.name,
          fileString: base64
        };
        this.checkIfPresent(documentObj);
        this.documents.push(documentObj);
        this.logger.info('Documents ', this.documents);
      });
      row.oldFileName = file.name;
      row.newFileAdded = true;
      row.fileExists = true;
      row.kycDoneBy = this.loggedInUser?.name || '';
      row.kycDateTime = this.formatDateTime(new Date());
      this.documentDataSource.next([...this.documentDataSource.getValue()]);
    }
  }

  private getDocumentType(documentCode: string): string {
    if (this.referenceName === 'Entity_Branch_KYC') {
      return 'Entity_Branch_' + documentCode;
    } else if (this.referenceName === 'Entity_KYC') {
      return 'Entity_' + documentCode;
    }
    return documentCode;
  }

  private getDefinitionCode(): string {
    if (this.referenceName === 'Entity_KYC') return '30';
    if (this.referenceName === 'Entity_Branch_KYC') return '31';
    return null;
  }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(btoa(reader.result as string));
      reader.onerror = err => reject(err);
      reader.readAsBinaryString(file);
    });
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType || 'application/octet-stream' });
  }

  areAllMandatoryDocAdded():boolean{
    const mandatoryDocs = this.documentDataSource.getValue().filter(item => item.isMandatory && !item.fileExists);
    this.logger.info(mandatoryDocs);
    let title = '';
    let i = 1;
    mandatoryDocs.forEach(item=>{
      title += (i++)+") "+item.documentName+"<br>";
    })
    if(title.length > 0){
      this.toastrService.error(title,"Following Documents are Mandatory:",{enableHtml:true});
    };
    return mandatoryDocs.length > 0 ? false : true;
  }

  checkIfPresent(documentObj){
    // Drop any previously staged file for the same row before adding the new one
    this.documents = (this.documents || []).filter(obj => obj._rowId !== documentObj._rowId);
  }

  isDocumentPresent(row):boolean{
    if (this.documentsOnEdit) {
      let selectedDocument = this.getDocument(row);
      if(selectedDocument && selectedDocument.fileName){
        row.newFileName = selectedDocument.originalFileName;
        row.fileExists = true;
        return true;
      }
    }
    return false;
  }

  uploadDocuments(referenceId): Observable<any> {
    if (this.documents && this.documents.length) {
      const uploads$ = this.documents.map(doc => {
        doc.parentId = referenceId;
        return this.service.post(Api.DOCUMENT_STORAGE_MULTI, [doc]);
      });
      this.documents = [];
      if (this.data?.branchId) {
        this.dailogRef?.close();
      }
      return forkJoin(uploads$);
    }
    if (this.data?.branchId) {
      this.dailogRef?.close();
    }
    return of(null);
  }
}
